export type EnumOption = {
  label: string;
  value: number;
};

// These values must match the C# enums in OlaCore/CoreShared/CoreEnums/SYS_Enums.cs
export const PATIENT_ACTIVATION_STATE = {
  UNKNOWN: 1,
  ACTIVE: 2,
  INACTIVE: 3,
} as const;

export const PATIENT_DECEASED_STATUS = {
  UNKNOWN: 1,
  ALIVE: 2,
  DECEASED: 3,
} as const;

export const PATIENT_DESEXED_STATUS = {
  UNKNOWN: 1,
  INTACT: 2,
  DE_SEXED: 3,
  NOT_APPLICABLE: 4,
} as const;

export const PATIENT_GENDER = {
  UNKNOWN: 1,
  MALE: 2,
  FEMALE: 3,
} as const;

export const PATIENT_SPECIES = {
  UNKNOWN: 1,
  AVIAN: 2,
  BOVINE: 3,
  CAMELID: 4,
  CANINE: 5,
  CAPRINE: 6,
  CAVIES: 7,
  CERVIDAE: 8,
  EQUINE: 9,
  FELINE: 10,
  LAPINE: 11,
  MURINE: 12,
  OVINE: 13,
  PISCINE: 14,
  PORCINE: 15,
  NOT_APPLICABLE: 16,
} as const;

export const PATIENT_BLOOD_GROUP = {
  UNKNOWN: 1,
  DEA_11: 2,
  DEA_11_PLUS: 3,
  A: 4,
  B: 5,
  AB: 6,
  C: 7,
  D: 8,
  K: 9,
  P: 10,
  Q: 11,
  U: 12,
  T: 13,
  NOT_APPLICABLE: 14,
} as const;

export const patientActivationOptions: EnumOption[] = [
  { label: "Active", value: PATIENT_ACTIVATION_STATE.ACTIVE },
  { label: "Inactive", value: PATIENT_ACTIVATION_STATE.INACTIVE },
];

export const patientSpeciesOptions: EnumOption[] = [
  { label: "Avian", value: PATIENT_SPECIES.AVIAN },
  { label: "Bovine", value: PATIENT_SPECIES.BOVINE },
  { label: "Camelid", value: PATIENT_SPECIES.CAMELID },
  { label: "Canine", value: PATIENT_SPECIES.CANINE },
  { label: "Caprine", value: PATIENT_SPECIES.CAPRINE },
  { label: "Cavies", value: PATIENT_SPECIES.CAVIES },
  { label: "Cervidae", value: PATIENT_SPECIES.CERVIDAE },
  { label: "Equine", value: PATIENT_SPECIES.EQUINE },
  { label: "Feline", value: PATIENT_SPECIES.FELINE },
  { label: "Lapine", value: PATIENT_SPECIES.LAPINE },
  { label: "Murine", value: PATIENT_SPECIES.MURINE },
  { label: "Ovine", value: PATIENT_SPECIES.OVINE },
  { label: "Piscine", value: PATIENT_SPECIES.PISCINE },
  { label: "Porcine", value: PATIENT_SPECIES.PORCINE },
  { label: "Not Applicable", value: PATIENT_SPECIES.NOT_APPLICABLE },
];

export const patientBreedOptions: EnumOption[] = [
  { label: "Breed 1", value: 1 },
  { label: "Breed 2", value: 2 },
];

// Backend validator rejects Unknown gender — only Male/Female are accepted.
export const patientGenderOptions: EnumOption[] = [
  { label: "Male", value: PATIENT_GENDER.MALE },
  { label: "Female", value: PATIENT_GENDER.FEMALE },
];

export const patientDesexedOptions: EnumOption[] = [
  { label: "Intact", value: PATIENT_DESEXED_STATUS.INTACT },
  { label: "De-Sexed", value: PATIENT_DESEXED_STATUS.DE_SEXED },
  { label: "Not Applicable", value: PATIENT_DESEXED_STATUS.NOT_APPLICABLE },
];

export const patientDeceasedOptions: EnumOption[] = [
  { label: "Alive", value: PATIENT_DECEASED_STATUS.ALIVE },
  { label: "Deceased", value: PATIENT_DECEASED_STATUS.DECEASED },
];

export const patientBloodGroupOptions: EnumOption[] = [
  { label: "DEA 11", value: PATIENT_BLOOD_GROUP.DEA_11 },
  { label: "DEA 11 Plus", value: PATIENT_BLOOD_GROUP.DEA_11_PLUS },
  { label: "A", value: PATIENT_BLOOD_GROUP.A },
  { label: "B", value: PATIENT_BLOOD_GROUP.B },
  { label: "AB", value: PATIENT_BLOOD_GROUP.AB },
  { label: "C", value: PATIENT_BLOOD_GROUP.C },
  { label: "D", value: PATIENT_BLOOD_GROUP.D },
  { label: "K", value: PATIENT_BLOOD_GROUP.K },
  { label: "P", value: PATIENT_BLOOD_GROUP.P },
  { label: "Q", value: PATIENT_BLOOD_GROUP.Q },
  { label: "U", value: PATIENT_BLOOD_GROUP.U },
  { label: "T", value: PATIENT_BLOOD_GROUP.T },
  { label: "Not Applicable", value: PATIENT_BLOOD_GROUP.NOT_APPLICABLE },
];

export const toSelectOptions = (options: EnumOption[]) =>
  options.map((option) => ({
    title: option.label,
    value: String(option.value),
  }));

/**
 * Look up the human-readable label for an enum value (number or numeric string).
 * Returns the original input as a string if no match is found, so callers
 * always get something printable.
 */
export const enumLabel = (
  options: EnumOption[],
  value: number | string | null | undefined
): string => {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const match = options.find((o) => o.value === n);
  return match ? match.label : String(value);
};

export const speciesLabel = (value: number | string | null | undefined) =>
  enumLabel(patientSpeciesOptions, value);

export const breedLabel = (value: number | string | null | undefined) =>
  enumLabel(patientBreedOptions, value);
